import React, { useEffect, useState } from "react";
import { useApp } from "@/context/AppContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { vendorCreateAd, vendorPayAd, vendorListAds, vendorPauseAd, vendorResumeAd, uploadAttachment, fetchAdSettings } from "@/lib/api";
import { toast } from "sonner";
import { AdCoverageList } from "@/components/vendor/AdInsights";
import { VendorAdReport } from "@/components/vendor/eye/VendorAdReport";
import { CampaignCompare } from "@/components/vendor/CampaignCompare";
import { AdCounters } from "@/components/vendor/AdCounters";
import { useAdStrings } from "@/lib/adStrings";
import { Loader2, Megaphone, CheckCircle2, Clock, AlertCircle, XCircle, PauseCircle, IndianRupee, Paperclip, X, Eye, EyeOff, ExternalLink } from "lucide-react";
import { Link } from "react-router-dom";

const parsePins = (txt) => [...new Set((txt.match(/\d+/g) || []))];
const pinValid = (p) => /^[1-9]\d{5}$/.test(p);
const ATTACH_ACCEPT = ".pdf,.doc,.docx,.jpg,.jpeg,.png,.mp4,.webm,.mov";
const ATTACH_LIMIT_MB = { pdf: 10, doc: 10, docx: 10, jpg: 8, jpeg: 8, png: 8, mp4: 20, webm: 20, mov: 20 };
const MAX_ATTACH = 5;

function AttachmentPicker({ files, setFiles }) {
  const M = useAdStrings().mgr;
  const [busy, setBusy] = useState(false);
  const pick = async (e) => {
    const chosen = Array.from(e.target.files || []);
    e.target.value = "";
    for (const f of chosen) {
      const ext = (f.name.split(".").pop() || "").toLowerCase();
      if (!ATTACH_LIMIT_MB[ext]) { toast.error(M.unsupported(f.name)); continue; }
      if (f.size > ATTACH_LIMIT_MB[ext] * 1024 * 1024) { toast.error(M.tooBig(f.name, ATTACH_LIMIT_MB[ext])); continue; }
      if (files.length >= MAX_ATTACH) { toast.error(M.maxAttach(MAX_ATTACH)); break; }
      setBusy(true);
      try {
        const up = await uploadAttachment(f, "ad");
        setFiles((list) => (list.length >= MAX_ATTACH ? list : [...list, up]));
      } catch (err) { toast.error(M.err ? M.uploadFail(f.name) : (err?.response?.data?.detail || M.uploadFail(f.name))); }
      finally { setBusy(false); }
    }
  };
  return (
    <div data-testid="ad-attachments">
      <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider block mb-1">{M.attach}</label>
      <label className={`inline-flex items-center gap-1.5 rounded-full border border-slate-200 hover:border-emerald-500 px-3 py-1.5 text-xs font-semibold cursor-pointer ${busy || files.length >= MAX_ATTACH ? "opacity-60 pointer-events-none" : ""}`} data-testid="ad-attach-btn">
        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Paperclip className="w-3.5 h-3.5" />} {M.attachBtn}
        <input type="file" multiple accept={ATTACH_ACCEPT} onChange={pick} className="hidden" data-testid="ad-attach-input" />
      </label>
      <div className="text-[11px] text-slate-400 mt-1">{M.attachHelp(MAX_ATTACH)}</div>
      {files.length > 0 && (
        <ul className="mt-2 space-y-1">
          {files.map((f) => (
            <li key={f.file_id} className="flex items-center gap-2 text-xs bg-slate-50 border border-slate-100 rounded-lg px-2 py-1.5" data-testid={`ad-attachment-${f.file_id}`}>
              <Paperclip className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
              <span className="flex-1 min-w-0 truncate">{f.filename}</span>
              <span className="text-slate-400 flex-shrink-0">{Math.max(1, Math.round(f.size / 1024))} KB</span>
              <button type="button" onClick={() => setFiles((list) => list.filter((x) => x.file_id !== f.file_id))} className="p-0.5 text-slate-400 hover:text-rose-600" aria-label={M.remove} data-testid={`ad-attachment-remove-${f.file_id}`}>
                <X className="w-3.5 h-3.5" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default function AdvertiseDialog({ open, onOpenChange, shops, activeShopId, target = null }) {
  const { pincode, formatApiError } = useApp();
  const M = useAdStrings().mgr;
  const apiErr = (err) => M.err || formatApiError(err);
  const [step, setStep] = useState("form"); // form | pay | done
  const [shopId, setShopId] = useState("");
  const [title, setTitle] = useState("");
  const [message, setMessage] = useState("");
  const [pin, setPin] = useState(pincode || "");
  const [cfg, setCfg] = useState({ price: 99, duration_days: 7, pricing_model: "per_pin", max_pins: 20 });
  const [busy, setBusy] = useState(false);
  const [ad, setAd] = useState(null);
  const [files, setFiles] = useState([]);

  useEffect(() => {
    if (!open) return;
    setStep("form"); setShopId(activeShopId || (shops && shops[0]?.shop_id) || ""); setTitle(""); setMessage(""); setPin(pincode || ""); setAd(null); setFiles([]);
    fetchAdSettings().then(setCfg).catch(() => {});
  }, [open, pincode, shops, activeShopId]);
  const pins = parsePins(pin);
  const badPins = pins.filter((p) => !pinValid(p));
  const total = cfg.price * (cfg.pricing_model === "per_pin" ? Math.max(pins.length, 1) : 1);

  const submit = async (e) => {
    e.preventDefault();
    if (!target && !shopId) { toast.error(M.errNoShop); return; }
    if (!title.trim()) { toast.error(M.errTitle); return; }
    if (!pins.length) { toast.error(M.errNoPin); return; }
    if (badPins.length) { toast.error(M.errBadPins(badPins.join(", "))); return; }
    if (pins.length > cfg.max_pins) { toast.error(M.errMaxPins(cfg.max_pins)); return; }
    setBusy(true);
    try {
      // Advertisement promotes the shop as a whole (mail/message style). The
      // backend accepts target_type=shop + target_id=shop_id. Title + message
      // are stored in the admin_note / description fields of the ad.
      const created = await vendorCreateAd({
        target_type: target ? target.type : "shop",
        target_id: target ? target.id : shopId,
        pincodes: pins,
        title: title.trim(),
        message: message.trim(),
        attachments: files.map((f) => f.file_id),
      });
      setAd(created);
      if (created.status === "active") { setStep("done"); toast.success("Free ad LIVE ho gaya"); } else setStep("pay");
    } catch (err) { toast.error(apiErr(err)); }
    finally { setBusy(false); }
  };

  const mockPay = async () => {
    if (!ad) return;
    setBusy(true);
    try {
      const paid = await vendorPayAd(ad.id);
      setAd(paid);
      setStep("done");
      toast.success(paid.status === "active" ? "Payment (mock) received — ad is LIVE now" : M.paidToast);
    } catch (err) { toast.error(apiErr(err)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[calc(100vw-1rem)] sm:w-full sm:max-w-md max-h-[92vh] overflow-y-auto p-4 sm:p-6" data-testid="advertise-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-display text-xl font-bold">
            <Megaphone className="w-5 h-5 text-emerald-700" /> {M.title}
          </DialogTitle>
          <p className="text-xs text-slate-500 mt-1">{M.promote(target ? target.name : (shops || []).find((s) => s.shop_id === shopId)?.name, cfg.duration_days)}</p>
        </DialogHeader>

        {step === "form" && (
          <form onSubmit={submit} className="space-y-4" data-testid="advertise-form">
            <div>
              <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider block mb-1">{M.adTitle}</label>
              <input value={title} onChange={(e) => setTitle(e.target.value)} maxLength={120} required placeholder={M.adTitlePh} className="w-full border border-slate-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-emerald-500" data-testid="ad-title" />
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider block mb-1">{M.msg}</label>
              <textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={3} maxLength={500} placeholder={M.msgPh} className="w-full border border-slate-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-emerald-500" data-testid="ad-message" />
            </div>
            <AttachmentPicker files={files} setFiles={setFiles} />
            <div>
              <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider block mb-1">{M.pins(cfg.max_pins)}</label>
              <textarea value={pin} onChange={(e) => setPin(e.target.value.replace(/[^\d,\s]/g, ""))} rows={2} inputMode="numeric" required placeholder={M.pinsPh} className="w-full border border-slate-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-emerald-500" data-testid="ad-pincode" />
              <div className="text-[11px] text-slate-400 mt-1">{M.pinsHelp}</div>
              {pins.length > 0 && (
                <div className="mt-1.5 flex flex-wrap gap-1" data-testid="ad-pin-chips">
                  {pins.map((p) => <span key={p} className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${pinValid(p) ? "bg-emerald-50 text-emerald-800" : "bg-rose-50 text-rose-700"}`} data-testid={`ad-pin-chip-${p}`}>{p}</span>)}
                </div>
              )}
              {badPins.length > 0 && <div className="text-[11px] text-rose-600 mt-1" data-testid="ad-pin-error">{M.invalidPin(badPins.join(", "))}</div>}
              {pins.length > cfg.max_pins && <div className="text-[11px] text-rose-600 mt-1" data-testid="ad-pin-limit">{M.maxPins(cfg.max_pins)}</div>}
            </div>
            <div className="rounded-xl border border-emerald-100 bg-emerald-50/60 p-3 text-xs text-emerald-900" data-testid="ad-total">
              {cfg.free_mode ? (
                <><span className="font-bold" data-testid="ad-free-label">Free</span> — abhi sabhi ads free hain. Submit karte hi ad {cfg.duration_days} din ke liye LIVE ho jayega, koi payment nahi.</>
              ) : (<>
              {M.total} <span className="font-bold">₹{total}</span>
              {cfg.pricing_model === "per_pin" ? M.perPin(cfg.price, Math.max(pins.length, 1)) : M.fixed}{M.forDays(cfg.duration_days)}
              </>)}
            </div>
            <button type="submit" disabled={busy} className="w-full rounded-full bg-emerald-700 hover:bg-emerald-800 text-white px-5 py-3 text-sm font-semibold inline-flex items-center justify-center gap-1.5 disabled:opacity-60" data-testid="ad-submit">
              {busy && <Loader2 className="w-4 h-4 animate-spin" />} {cfg.free_mode ? "Submit — Free ad LIVE karein" : M.review}
            </button>
          </form>
        )}

        {step === "pay" && ad && (
          <div className="space-y-4" data-testid="advertise-pay-step">
            <div className="rounded-xl border border-slate-200 p-3">
              <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">{M.reviewH}</div>
              <div className="text-sm text-slate-800 mt-1">{M.promoting} <b>{ad.preview?.name || ad.target_id}</b> ({M.type[ad.target_type] || ad.target_type}) · {M.inPin} <b>{(ad.pincodes || [ad.pincode]).join(", ")}</b> · {M.forW} <b>{ad.duration_days} {M.days}</b> · {M.totalW} <b data-testid="ad-review-total">₹{ad.price}</b>.</div>
              <AdCoverageList pins={ad.pincodes || [ad.pincode]} />
              {(ad.attachments || []).length > 0 && <div className="text-xs text-slate-500 mt-1" data-testid="ad-review-attachments">{M.attCount(ad.attachments.length)} {ad.attachments.map((a) => a.filename).join(", ")}</div>}
            </div>
            <div className="rounded-xl border border-amber-200 bg-amber-50/60 p-3 text-xs text-amber-900">
              <AlertCircle className="w-4 h-4 inline mr-1 -mt-0.5" />
              <span data-testid="ad-mock-note">{M.mockNote(ad.price)}</span>
            </div>
            <button onClick={mockPay} disabled={busy} className="w-full rounded-full bg-emerald-700 hover:bg-emerald-800 text-white px-5 py-3 text-sm font-semibold inline-flex items-center justify-center gap-1.5 disabled:opacity-60" data-testid="ad-pay-btn">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <IndianRupee className="w-4 h-4" />} {M.pay(ad?.price)}
            </button>
            <button onClick={() => setStep("form")} className="w-full rounded-full border border-slate-200 text-slate-600 px-5 py-2 text-xs font-semibold" data-testid="ad-back-btn">{M.back}</button>
          </div>
        )}

        {step === "done" && (
          <div className="space-y-3 text-center" data-testid="advertise-done-step">
            <CheckCircle2 className="w-14 h-14 text-emerald-600 mx-auto" />
            <div className="font-display text-xl font-bold">{ad?.status === "active" ? "Ad LIVE ho gaya" : M.doneH}</div>
            <p className="text-sm text-slate-500" data-testid="ad-done-msg">{ad?.status === "active" ? (ad?.free ? "Free ad turant LIVE ho gaya. Koi payment nahi li gayi." : "Ad turant LIVE ho gaya (testing mode: Admin approval abhi band hai).") : M.doneP}</p>
            <button onClick={() => onOpenChange(false)} className="rounded-full bg-emerald-700 hover:bg-emerald-800 text-white px-5 py-2 text-sm font-semibold" data-testid="ad-done-btn">{M.done}</button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export function AdStatusBadge({ status }) {
  const L = useAdStrings().mgr.status;
  const map = {
    pending_payment: { icon: Clock, cls: "bg-slate-100 text-slate-700", label: "Pending payment" },
    pending_approval: { icon: Clock, cls: "bg-amber-100 text-amber-800", label: "Pending approval" },
    active: { icon: CheckCircle2, cls: "bg-emerald-100 text-emerald-800", label: "Active" },
    rejected: { icon: XCircle, cls: "bg-rose-100 text-rose-800", label: "Rejected" },
    paused: { icon: PauseCircle, cls: "bg-sky-100 text-sky-800", label: "Paused" },
    expired: { icon: AlertCircle, cls: "bg-slate-100 text-slate-500", label: "Completed" },
  };
  const m = map[status] ? { ...map[status], label: L[status] || map[status].label } : { icon: AlertCircle, cls: "bg-slate-100 text-slate-700", label: status || "" };
  const Icon = m.icon;
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-bold uppercase tracking-wider ${m.cls}`} data-testid={`ad-status-${status}`}>
      <Icon className="w-3 h-3" /> {m.label}
    </span>
  );
}

export function VendorAdsList({ refreshKey }) {
  const [ads, setAds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [openReport, setOpenReport] = useState(null);
  const M = useAdStrings().mgr;
  useEffect(() => {
    setLoading(true);
    vendorListAds().then(setAds).catch(() => setAds([])).finally(() => setLoading(false));
  }, [refreshKey]);
  const pauseResume = async (a) => {
    setBusy(a.id);
    try {
      const updated = a.status === "active" ? await vendorPauseAd(a.id) : await vendorResumeAd(a.id);
      setAds((list) => list.map((x) => (x.id === a.id ? updated : x)));
      toast.success(updated.status === "active" ? M.resumed : M.paused);
    } catch (e) {
      toast.error(M.err ? M.updErr : (e?.response?.data?.detail || M.updErr));
      vendorListAds().then(setAds).catch(() => {});
    } finally { setBusy(null); }
  };
  if (loading) return <div className="text-center text-slate-500 py-8 text-sm">{M.loading}</div>;
  if (ads.length === 0) return <div className="text-center text-slate-500 py-8 text-sm" data-testid="vendor-ads-empty">{M.empty}</div>;
  return (
    <div className="space-y-2" data-testid="vendor-ads-list">
      {ads.length >= 2 && <CampaignCompare ads={ads} />}
      {ads.map((a) => (
        <div key={a.id} className="border border-slate-100 rounded-xl p-3" data-testid={`vendor-ad-${a.id}`}>
        <div className="flex items-center gap-3">
          <img src={a.preview?.image || ""} alt="" className="w-12 h-12 rounded-lg object-cover bg-slate-100 flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold truncate">{a.preview?.name || a.target_id} <span className="text-slate-400 font-normal">· {M.type[a.target_type] || a.target_type}</span></div>
            {a.title && <div className="text-xs text-slate-700 truncate" data-testid={`vendor-ad-title-${a.id}`}>{a.title}</div>}
            <div className="text-[11px] text-slate-500 truncate">PIN {(a.pincodes || [a.pincode]).join(", ")} · {a.duration_days}{M.d} · {a.free ? "Free" : `₹${a.price} (${M.mock})`}{(a.attachments || []).length ? ` · ${M.files(a.attachments.length)}` : ""}</div>
            <div className="text-[10px] text-slate-400 truncate" data-testid={`vendor-ad-id-${a.id}`}>ID {a.id}{a.created_at ? ` · ${new Date(a.created_at).toLocaleString()}` : ""}</div>
          </div>
          <div className="flex flex-col items-end gap-1.5 flex-shrink-0">
            <AdStatusBadge status={a.status} />
            {a.status === "rejected" && (a.admin_note || "").trim() && (
              <span className="max-w-[11rem] text-right text-[11px] text-rose-700 line-clamp-2" title={a.admin_note} data-testid={`vendor-ad-reject-reason-${a.id}`}>{a.admin_note.trim()}</span>
            )}
            {(a.status === "active" || (a.status === "paused" && a.paused_by === "vendor")) && (
              <button onClick={() => pauseResume(a)} disabled={busy === a.id} className="text-[11px] font-semibold rounded-full border border-slate-200 hover:border-emerald-400 px-2.5 py-1 disabled:opacity-50" data-testid={`ad-${a.status === "active" ? "pause" : "resume"}-${a.id}`}>
                {busy === a.id ? "..." : a.status === "active" ? M.pause : M.resume}
              </button>
            )}
            <Link to={`/ad/${a.id}`} className="inline-flex items-center gap-1 text-[11px] font-semibold rounded-full border border-slate-200 hover:border-emerald-400 text-slate-700 px-2.5 py-1" data-testid={`ad-view-btn-${a.id}`}>
              <ExternalLink className="w-3.5 h-3.5" /> Ad dekhein
            </Link>
            <button onClick={() => setOpenReport(openReport === a.id ? null : a.id)} aria-label="View advertisement performance report" aria-expanded={openReport === a.id}
              className="inline-flex items-center gap-1 text-[11px] font-semibold rounded-full border border-emerald-200 bg-emerald-50 text-[rgb(var(--vb-primary-dark))] hover:bg-emerald-100 px-2.5 py-1" data-testid={`ad-report-btn-${a.id}`}>
              {openReport === a.id ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />} {openReport === a.id ? M.hideReport : M.viewReport}
            </button>
          </div>
        </div>
        <AdCounters counters={a.counters} testid={`vendor-ad-counters-${a.id}`} />
        {openReport === a.id && <VendorAdReport adId={a.id} canShare />}
        </div>
      ))}
    </div>
  );
}
